import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, FormControlLabel, Grid, InputLabel, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { get } from "utils/WebService";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };

export const PACOTE_VAZIO = {
    codigoNovo: "",
    zerarDebitos: false,
    debitoTitulo: "EXECUÇÃO",
    // A ação do cadastro (Cadastro Geral > Ações) escolhida no Débito: vai também para o Tipo de Ação do processo
    debitoCodAcao: null,
    debitoVencimento: "",
    debitoValor: "",
    processoNumero: "",
    processoData: "",
    processoValor: "",
    processoGcpj: "",
};

/** "30625.83" -> "30.625,83" para o campo de valor. */
const valorParaCampo = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
};

/** O pacote gravado no pedido (datas yyyy-MM-dd, valores com ponto) para os campos da tela. */
export function pacoteParaCampos(p) {
    if (!p) return { ...PACOTE_VAZIO };
    return {
        codigoNovo: p.codigoNovo || "",
        zerarDebitos: !!p.zerarDebitos,
        debitoTitulo: p.debitoTitulo || PACOTE_VAZIO.debitoTitulo,
        debitoCodAcao: p.debitoCodAcao || null,
        debitoVencimento: p.debitoVencimento || "",
        debitoValor: valorParaCampo(p.debitoValor),
        processoNumero: p.processoNumero || "",
        processoData: p.processoData || "",
        processoValor: valorParaCampo(p.processoValor),
        processoGcpj: p.processoGcpj || "",
    };
}

/** Os campos da tela para o envio; null quando nada foi preenchido (migração comum). */
export function pacoteParaEnvio(c) {
    if (!c) return null;
    const temDebito = !!(c.debitoVencimento || String(c.debitoValor || "").trim());
    const temProcesso = !!(String(c.processoNumero || "").trim() || c.processoData || String(c.processoValor || "").trim() || String(c.processoGcpj || "").trim());
    const codigo = String(c.codigoNovo || "").trim();
    if (!codigo && !c.zerarDebitos && !temDebito && !temProcesso) return null;
    return {
        codigoNovo: codigo || null,
        zerarDebitos: !!c.zerarDebitos,
        debitoTitulo: temDebito ? (String(c.debitoTitulo || "").trim() || PACOTE_VAZIO.debitoTitulo) : null,
        debitoCodAcao: temDebito || temProcesso ? (c.debitoCodAcao || null) : null,
        debitoVencimento: c.debitoVencimento || null,
        debitoValor: String(c.debitoValor || "").trim() || null,
        processoNumero: String(c.processoNumero || "").trim() || null,
        processoData: c.processoData || null,
        processoValor: String(c.processoValor || "").trim() || null,
        processoGcpj: String(c.processoGcpj || "").trim() || null,
    };
}

/**
 * Dados do Jurídico da migração para uma carteira jurídica: código do cliente novo (só no cliente habilitado), zerar os
 * débitos atuais, lançar o débito do processo e o processo da ficha (com o Nº GCPJ). Na solicitação, quem pede preenche e
 * o suporte confere e edita na aprovação; nada é aplicado até a migração ser efetivada. O nome do débito sai da lista de
 * Ações (Cadastro Geral > Ações, a mesma do Tipo de Ação da aba Jurídico) ou é digitado; a ação escolhida vai também para
 * o Tipo de Ação do processo.
 */
export default function PacoteJuridicoMigracao({ dados, valor, onChange, disabled, carteiraId, ajuda }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const v = valor || PACOTE_VAZIO;
    const [acoes, setAcoes] = useState([]);
    // Só as ações habilitadas para as equipes da carteira de destino (Cadastro Geral > Ações)
    useEffect(() => {
        get(`/processos-transitorios/acoes${carteiraId ? `?carteira=${carteiraId}` : ""}`)
            .then(res => setAcoes(Array.isArray(res) ? res : [])).catch(() => setAcoes([]));
    }, [carteiraId]);
    const acaoEscolhida = acoes.find(a => a.id === v.debitoCodAcao) || null;
    const muda = (campo) => (e) => onChange({ ...v, [campo]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });
    const trocaCodigo = !!dados?.clienteTrocaCodigo || !!v.codigoNovo;
    const podeCopiar = (v.debitoVencimento || v.debitoValor) && !v.processoData && !v.processoValor;

    return (
        <MDBox p={1.5} mb={2} sx={{ border: `1px solid ${cores.borda}`, borderRadius: "8px", backgroundColor: cores.painel }}>
            <MDTypography variant="button" fontWeight="bold" display="block">Dados do Jurídico</MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
                {ajuda || "O que fazer na ficha ao migrar para a carteira jurídica. Deixe em branco o que não se aplica."}
            </MDTypography>
            <Grid container spacing={1.5}>
                {trocaCodigo ? (
                    <Grid item xs={12} sm={6}>
                        <InputLabel sx={rotulo}>Código do cliente novo</InputLabel>
                        <MDInput fullWidth size="small" value={v.codigoNovo} onChange={muda("codigoNovo")} disabled={disabled}
                            placeholder={dados?.codigoAtual ? `Atual: ${dados.codigoAtual}` : ""} />
                        <MDTypography variant="caption" color="text" display="block" px={1}>
                            {dados?.codigoAtual ? `Atual: ${dados.codigoAtual} (fica guardado no devedor). ` : ""}Em branco: mantém o atual.
                        </MDTypography>
                    </Grid>
                ) : dados && (
                    <Grid item xs={12}>
                        <MDTypography variant="caption" color="text" display="block" px={1}>
                            Código do cliente: {dados.codigoAtual || "-"} (o cliente {dados.nomeCliente || ""} não troca o código na migração).
                        </MDTypography>
                    </Grid>
                )}
                <Grid item xs={12} sm={trocaCodigo ? 6 : 12}>
                    <FormControlLabel
                        sx={{ ml: 0, mt: trocaCodigo ? 2.5 : 0 }}
                        control={<Switch checked={!!v.zerarDebitos} onChange={muda("zerarDebitos")} disabled={disabled} />}
                        label={<MDTypography variant="button" fontWeight="regular" color="text" ml={1}>Zerar os débitos atuais</MDTypography>}
                    />
                    <MDTypography variant="caption" color="text" display="block" px={1}>
                        Os débitos em aberto que migram ficam com a situação Migrado e saldo zero (continuam no histórico).
                    </MDTypography>
                </Grid>

                <Grid item xs={12}><MDTypography variant="caption" fontWeight="bold" color="text" px={1}>Lançar débito</MDTypography></Grid>
                <Grid item xs={12} sm={5}>
                    <InputLabel sx={rotulo}>Débito</InputLabel>
                    <Autocomplete
                        freeSolo
                        disabled={disabled}
                        options={acoes}
                        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.nome || "")}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        value={acaoEscolhida || v.debitoTitulo || null}
                        inputValue={v.debitoTitulo || ""}
                        onInputChange={(_, texto, motivo) => {
                            if (motivo === "reset") return;
                            // Digitou: vale o texto; se for igual ao nome de uma ação da lista, fica com ela
                            const igual = acoes.find(a => (a.nome || "").toUpperCase() === texto.trim().toUpperCase());
                            onChange({ ...v, debitoTitulo: texto, debitoCodAcao: igual ? igual.id : null });
                        }}
                        onChange={(_, escolha) => {
                            if (escolha && typeof escolha === "object") onChange({ ...v, debitoTitulo: escolha.nome, debitoCodAcao: escolha.id });
                            else onChange({ ...v, debitoTitulo: typeof escolha === "string" ? escolha : "", debitoCodAcao: null });
                        }}
                        renderInput={(params) => <MDInput {...params} size="small" placeholder="Escolha a ação ou digite" />}
                    />
                    <MDTypography variant="caption" color="text" display="block" px={1}>
                        {acaoEscolhida ? "Vai também como Tipo de Ação do processo."
                            : acoes.length === 0 ? "Nenhuma ação habilitada para a equipe desta carteira (Cadastro Geral > Ações): digite o nome."
                                : "Escolha na lista ou digite o nome."}
                    </MDTypography>
                </Grid>
                <Grid item xs={6} sm={3.5}>
                    <InputLabel sx={rotulo}>Vencimento</InputLabel>
                    <MDInput fullWidth size="small" type="date" value={v.debitoVencimento} onChange={muda("debitoVencimento")} disabled={disabled} />
                </Grid>
                <Grid item xs={6} sm={3.5}>
                    <InputLabel sx={rotulo}>Valor (R$)</InputLabel>
                    <MDInput fullWidth size="small" value={v.debitoValor} onChange={muda("debitoValor")} disabled={disabled} placeholder="Ex.: 30.625,83" />
                </Grid>

                <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <MDTypography variant="caption" fontWeight="bold" color="text" px={1}>Processo da ficha</MDTypography>
                    {podeCopiar && !disabled && (
                        <MDTypography variant="caption" color="info" sx={{ cursor: "pointer", textDecoration: "underline", px: 1 }}
                            onClick={() => onChange({ ...v, processoData: v.debitoVencimento, processoValor: v.debitoValor })}>
                            Usar a data e o valor do débito
                        </MDTypography>
                    )}
                </Grid>
                <Grid item xs={12} sm={5}>
                    <InputLabel sx={rotulo}>Nº do processo</InputLabel>
                    <MDInput fullWidth size="small" value={v.processoNumero} onChange={muda("processoNumero")} disabled={disabled}
                        placeholder="0000000-00.0000.0.00.0000" />
                </Grid>
                <Grid item xs={6} sm={3.5}>
                    <InputLabel sx={rotulo}>Data da distribuição</InputLabel>
                    <MDInput fullWidth size="small" type="date" value={v.processoData} onChange={muda("processoData")} disabled={disabled} />
                </Grid>
                <Grid item xs={6} sm={3.5}>
                    <InputLabel sx={rotulo}>Valor (R$)</InputLabel>
                    <MDInput fullWidth size="small" value={v.processoValor} onChange={muda("processoValor")} disabled={disabled} />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <InputLabel sx={rotulo}>Nº GCPJ</InputLabel>
                    <MDInput fullWidth size="small" value={v.processoGcpj} onChange={muda("processoGcpj")} disabled={disabled} />
                </Grid>
                <Grid item xs={12} sm={7} display="flex" alignItems="flex-end">
                    <MDTypography variant="caption" color="text" px={1} pb={0.5}>
                        Mesmo número já na ficha: o processo é atualizado, não duplicado.
                    </MDTypography>
                </Grid>
            </Grid>
        </MDBox>
    );
}

PacoteJuridicoMigracao.propTypes = {
    dados: PropTypes.shape({
        codigoAtual: PropTypes.string,
        clienteTrocaCodigo: PropTypes.bool,
        nomeCliente: PropTypes.string,
    }),
    valor: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    carteiraId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Texto de apoio abaixo do título (ex.: na aprovação, que em branco o CobJur completa depois)
    ajuda: PropTypes.string,
};

PacoteJuridicoMigracao.defaultProps = {
    dados: null,
    valor: null,
    disabled: false,
    carteiraId: null,
    ajuda: null,
};
